import { memo, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Skeleton } from '@strivelabs/ui-core/v2';
import { formatBytes } from '@strivelabs/ui-blocks';
import {
  ListTable,
  ListTableDate,
  ListTablePerson,
  ListTableText,
  ListTableTitle,
  type ListTableColumn,
} from '@strivelabs/ui-blocks/v2';
import { fileContentUrl, type LibraryFile } from '@/services/DataService';
import { FILE_THUMBNAIL_SIZE } from '@/constants/fileUpload';
import { LIBRARY_ROW_HEIGHT } from '@/constants/library';
import { FileActionsMenu } from './FileActionsMenu';
import { FileGlyph } from './FileGlyph';
import { LibraryImage } from './LibraryImage';
import { entryShape, isImageType, typeLabel, type LibraryEntry } from './libraryEntry';

interface LibraryListProps {
  entries: LibraryEntry[];
  /** The search in force, marked inside the names it matched. */
  query: string;
  /** The file open in the side panel, marked in the list. */
  selectedId?: string | null;
  onOpen: (file: LibraryFile) => void;
  onAsk: (file: LibraryFile) => void;
  onDownload: (file: LibraryFile) => void;
}

/** A thumbnail's frame in a row: a rounded square the image fills or the mark sits in. */
const THUMB = 'flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-v2-md border border-v2-list-line bg-v2-list-tabbar';

/**
 * The picture a row's thumbnail shows: the stored file's smallest thumbnail,
 * an upload's local preview, or none for a file without a picture.
 *
 * @param entry - The row's entry.
 *
 * @returns The source, and whether it is a preview still uploading.
 */
const thumbnailOf = (entry: LibraryEntry): { src: string; uploading: boolean } | null => {
  if (!isImageType(entryShape(entry).mime_type)) return null;
  if (entry.kind === 'file') return { src: fileContentUrl(entry.file.id, FILE_THUMBNAIL_SIZE.CHIP), uploading: false };
  return entry.upload.previewUrl ? { src: entry.upload.previewUrl, uploading: true } : null;
};

/**
 * What a row shows of a file at the front of its name: the picture, or the
 * file's mark. Shared with the composer's library picker, whose rows are this
 * list's in miniature.
 */
export const LibraryThumbnail = ({ entry }: { entry: LibraryEntry }) => {
  const thumbnail = thumbnailOf(entry);
  const glyph = <FileGlyph mimeType={entryShape(entry).mime_type} className="size-4" />;

  return (
    <span className={THUMB}>
      {thumbnail ? (
        <LibraryImage
          src={thumbnail.src}
          alt=""
          eager={thumbnail.uploading}
          className={thumbnail.uploading ? 'opacity-60' : undefined}
          fallback={glyph}
        />
      ) : glyph}
    </span>
  );
};

/**
 * The list's columns, built once per search: the name with its thumbnail, then
 * what a reader scans a library by — type, size, who uploaded it, and when it
 * last changed.
 *
 * @param query - The search in force.
 * @param onAsk - Opens a new chat with a file attached.
 * @param onDownload - Downloads a file.
 *
 * @returns The columns.
 */
const columnsFor = (
  query: string,
  onAsk: (file: LibraryFile) => void,
  onDownload: (file: LibraryFile) => void,
): ListTableColumn<LibraryEntry>[] => [
  {
    id: 'name',
    header: 'Name',
    minWidth: 240,
    cell: (entry) => (
      <span className="flex min-w-0 items-center gap-3">
        <LibraryThumbnail entry={entry} />
        <ListTableTitle query={query} subtitle={entry.kind === 'pending' ? 'Uploading…' : undefined}>
          {entryShape(entry).filename}
        </ListTableTitle>
      </span>
    ),
  },
  {
    id: 'type',
    header: 'Type',
    width: 80,
    cell: (entry) => <ListTableText>{typeLabel(entryShape(entry).mime_type)}</ListTableText>,
  },
  {
    id: 'size',
    header: 'Size',
    width: 96,
    cell: (entry) => <ListTableText>{formatBytes(entryShape(entry).size_bytes)}</ListTableText>,
  },
  {
    id: 'uploaded_by',
    header: 'Uploaded by',
    width: 180,
    hideWhenLean: true,
    cell: (entry) => (entry.kind === 'file' ? (
      <ListTablePerson name={entry.file.uploaded_by?.display_name} imageUrl={entry.file.uploaded_by?.image_url} />
    ) : null),
  },
  {
    // Files cannot be edited yet, so this is the upload; it will mean an edit
    // too once they can.
    id: 'modified',
    header: 'Modified',
    width: 110,
    cell: (entry) => (entry.kind === 'file'
      ? <ListTableDate date={entry.file.updated_at} cardPrefix="Modified" />
      : null),
  },
  {
    id: 'actions',
    header: <span className="sr-only">Actions</span>,
    width: 40,
    cell: (entry) => (entry.kind === 'file' ? (
      <FileActionsMenu
        filename={entry.file.filename}
        onAsk={() => onAsk(entry.file)}
        onDownload={() => onDownload(entry.file)}
      />
    ) : (
      <span className="inline-flex size-7 items-center justify-center text-v2-list-faint" aria-label="Uploading">
        <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />
      </span>
    )),
  },
];

/**
 * The library as a list: the shared list table, its rows tall enough for a
 * thumbnail. A row opens its file; an upload's row does nothing until it is
 * stored.
 */
export const LibraryList = memo(({
  entries, query, selectedId, onOpen, onAsk, onDownload,
}: LibraryListProps) => {
  const columns = useMemo(() => columnsFor(query, onAsk, onDownload), [ query, onAsk, onDownload ]);

  return (
    <ListTable
      ariaLabel="Files"
      stickyHeader
      columns={columns}
      rows={entries}
      getRowId={(entry) => entry.id}
      rowLabel={(entry) => entryShape(entry).filename}
      rowHeight={LIBRARY_ROW_HEIGHT}
      selectedRowId={selectedId ?? undefined}
      onRowClick={(entry) => {
        if (entry.kind === 'file') {
          onOpen(entry.file);
        }
      }}
    />
  );
});

LibraryList.displayName = 'LibraryList';

/** The list while the first page loads: rows at the list's own height, each a thumbnail and a name. */
export const LibraryListSkeleton = () => (
  <div className="flex flex-col px-[18px] pb-3 pt-2" role="status" aria-busy="true" aria-label="Loading files">
    {Array.from({ length: 8 }, (_unused, row) => (
      <div key={row} className="flex items-center gap-3" style={{ height: LIBRARY_ROW_HEIGHT }}>
        <Skeleton className="size-9 shrink-0 rounded-v2-md" />
        <Skeleton className="h-3.5 flex-1" style={{ maxWidth: `${40 + ((row * 17) % 35)}%` }} />
      </div>
    ))}
  </div>
);
